import { Logger } from 'Errors/Logger';
import { ObjectUtils } from 'Helpers';
import { getProduitPrincipal, getProduitSecondaire } from 'Web/Pages/Quote/Utils/UpdateQuestions';

import { IRoom, RoomType, StairsSteps, StairsStepType } from '../../Models/IRoom';

export class StairsModeDeCalcul {

    public static setBaseDeCalcul = (items: IRoom[]) => {
        items.forEach((stairs) => {
            if (stairs.type === RoomType.Stairs) {
                const stairs_tread_count = this.calcStairsTreadCount(stairs.stairsSteps || []);
                stairs.stairsBaseDeCalculs = {
                    stairs_total_marches_droites: ObjectUtils.sum(stairs.stairsSteps?.filter(step => step.type === StairsStepType.MarcheDroite), step => step.quantity),
                    stairs_total_marches_tournantes: ObjectUtils.sum(stairs.stairsSteps?.filter(step => step.type === StairsStepType.MarcheTournante), step => step.quantity),
                    stairs_total_marches_arrondies: ObjectUtils.sum(stairs.stairsSteps?.filter(step => step.type === StairsStepType.MarcheArrondie), step => step.quantity),
                    stairs_total_paliers: ObjectUtils.sum(stairs.stairsSteps?.filter(step => step.type === StairsStepType.Palier), step => step.quantity),
                    //* Nombre de marches : StairsMarchesCount
                    stairs_tread_count,
                    //* Nombre de contremarches : StairsRisersCount
                    stairs_riser_count: stairs.hasContremarches ? stairs_tread_count + 1 : 0,
                    //* Surface d’un escalier en m²: : StairsSurface (attention il y a aussi pour les produit en paquet : Surface + marge 100%)
                    stairs_surface: this.calcStairsSurface(stairs.stairsSteps || []),
                    //* Surface des marches en m² :StairsStepsSurface
                    stairs_treads_surface: this.calcStairsTreadsSurface(stairs.stairsSteps || []),
                    //* Surface des contremarches en m²: StairsRisersSurface
                    stairs_risers_surface: stairs.hasContremarches ? this.calcStairsRisersSurface(stairs.stairsSteps || []) : 0,
                    //* Longueur d’un escalier : StairsLength
                    stairs_length: this.calcStairsLength(stairs.stairsSteps || []),
                };

                // ➤ Vérifie la compatibilité produit si fourni
                const incompatible = this.isProductIncompatibleWithPassage(stairs);
                stairs.incompatibleWithPassage = incompatible;
            }
        });
    }

    //* Nombre de marches
    private static calcStairsTreadCount = (stairsSteps: StairsSteps[]) => ObjectUtils.sum(stairsSteps, step => step.quantity);

    //* Surface d’un escalier en m²:
    private static calcStairsSurface = (stairsSteps: StairsSteps[]) => {
        const totalSurface = ObjectUtils.sum(stairsSteps, step => {
            const N = step.quantity;                      //!\\ Nombre total de marches
            const G = (step.giron || 0) / 100;            //!\\ Giron (profondeur de marche en mètre)
            const L = (step.marches || 0) / 100;          //!\\ Largeur de la marche en mètre
            const H = (step.contremarches || 0) / 100;    //!\\ Hauteur de la contremarche en mètre
            return N * L * (G + H);
        });
        return parseFloat(totalSurface.toFixed(2)); // arrondi à 2 décimales
    }

    //* Surface des marches en m²
    // Calcul de la surface des marches
    // La surface des marches est calculée en multipliant le nombre de marches
    // par la largeur de la marche et la profondeur de marche.
    // Nombre de marches X Largeur des marches X Giron (profondeur) des marches
    private static calcStairsTreadsSurface = (stairsSteps: StairsSteps[]): number => {
        const surface = ObjectUtils.sum(stairsSteps, step => {
            const N = step.quantity;        // Nombre total de marches
            const L = step.marches / 100;   // Largeur des marches
            const G = step.giron / 100;     // Profondeur des marches
            return (N * L * G);
        });
        return parseFloat((surface).toFixed(2));
    };

    //* Surface des contremarches en m²
    // Calcul de la surface des contremarches
    // La surface des contremarches est calculée en multipliant le nombre de marches
    // par la hauteur de la contremarche et la profondeur de marche.
    // Nombre de marches X Largeur des marches X Hauteur des contremarches
    private static calcStairsRisersSurface = (stairsSteps: StairsSteps[]) => {
        const surface = ObjectUtils.sum(stairsSteps, step => {
            const N = step.quantity;                    // nombre total de marches
            const L = (step.marches || 0) / 100;        // largeur des marches
            const H = (step.contremarches || 0) / 100;    // hauteur des contremarches
            return (N * L * H);
        });
        return parseFloat((surface).toFixed(2));
    }

    private static calcStairsLength = (stairsSteps: StairsSteps[]) => {
        return ObjectUtils.sum(stairsSteps, step => this.calcul_longueur_passage(step.quantity, step.giron, step.contremarches || 0));
    }

    private static calcul_longueur_passage = (nb_marches: number, giron_cm: number, contremarche_cm: number) => {
        // Calcule la longueur en mètres linéaires d'un passage d'escalier
        // qui recouvre chaque giron et chaque contremarche + une contremarche supplémentaire.
        // :param nb_marches: nombre de marches
        // :param giron_cm: profondeur (giron) en cm
        // :param contremarche_cm: hauteur de contremarche en cm
        // :return: longueur en mètres linéaires
        const longueur_cm = (nb_marches * (giron_cm + contremarche_cm)) + contremarche_cm;
        const longueur_ml = longueur_cm / 100;  //!\\ conversion en mètres
        return parseFloat(longueur_ml.toFixed(2)); // arrondi à 2 décimales
    }

    //* Vérifie si la largeur max de marche > LARGEUR_M du produit
    private static isProductIncompatibleWithPassage(stairs: IRoom) {
        const { stairsSteps: steps = [] } = stairs;
        const { produitPrincipal: product } = getProduitPrincipal(stairs)

        if (!steps.length || !product?.attributs?.length) {
            return false;
        }

        // largeur passage (m → cm)
        const largeurPassageM = Number(product.attributs.find(a => a.name === "S029-Largeur")?.value);
        if (!largeurPassageM) {
            Logger.log(`⚠️ Produit ${product.code} sans largeur S029-Largeur.`);
            return false; // on considère incompatible si non défini
        }

        const largeurPassageCm = largeurPassageM * 100;

        // Recherche de la plus grande marche (en mètres)
        const plusGrandeMarche = Math.max(...steps.map(step => step.marches ?? 0));

        if (plusGrandeMarche > largeurPassageCm) {
            Logger.log(`❌ [${stairs.roomId} - ${stairs.name}] Incompatible : plus grande marche ${plusGrandeMarche} cm > passage ${largeurPassageCm} cm (produit ${product.code})`);
            return true;
        }

        return false;
    }


}